// Certificate verification panel – looks up a certificate by its verification token
import { useState } from 'react'
import { ShieldCheck, Search, XCircle, CheckCircle2, Clock } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/card'
import { Button } from '@/ui/button'
import { getCertificateStatus } from '@/features/classes/stores'
import type { Certificate } from '@/features/classes/stores'
import { fetchCourseBySlug } from '@/core/api/coursesService'
import { cn } from '@/core/lib/utils'

import { inputClass, fmtDate } from '../utils/formatters'
import { getCertificateByToken as pbGetCertificateByToken } from '../api/certificatesService'
import { getAttendee } from '../api/attendeesService'
import { getSession } from '../api/sessionsService'

function formatStudentName(attendee: { firstName?: string; lastName?: string; studentId: string }): string {
  const first = attendee.firstName?.trim()
  const last = attendee.lastName?.trim()
  const full = [first, last].filter(Boolean).join(' ')
  if (full) {return full}
  // Fall back to the local part of the email
  return attendee.studentId.split('@')[0] ?? attendee.studentId
}

type VerifyResult =
  | { found: true; status: 'valid' | 'expired' | 'revoked'; cert: Certificate; name: string; course: string }
  | { found: false }

export function VerifyPanel() {
  const [token, setToken] = useState('')
  const [result, setResult] = useState<VerifyResult | null>(null)
  const [verifying, setVerifying] = useState(false)

  async function handleVerify() {
    if (!token.trim()) {return}
    setVerifying(true)
    try {
      const pbCert = await pbGetCertificateByToken(token.trim())
      if (!pbCert) {
        setResult({ found: false })
        return
      }
      const cert: Certificate = {
        id: pbCert.id,
        attendeeId: pbCert.attendeeId,
        certificateNumber: pbCert.certificateNumber,
        issuedAt: pbCert.issuedAt,
        expiresAt: pbCert.expiresAt,
        verificationToken: pbCert.verificationToken,
        revoked: pbCert.revoked,
      }

      // Fetch attendee & session from API so verification works after a reload
      let studentName = cert.attendeeId
      let courseName = 'Unknown'
      try {
        const pbAttendee = await getAttendee(cert.attendeeId)
        studentName = formatStudentName(pbAttendee)
        const pbSession = await getSession(pbAttendee.sessionId)
        const course = await fetchCourseBySlug(pbSession.courseSlug ?? pbSession.courseId)
        courseName = course?.name ?? pbSession.courseName ?? pbSession.courseId ?? 'Unknown'
      } catch (err) {
        console.warn('Could not fetch attendee/session for verification:', err)
        // Fall back to attendeeId/Unknown
      }

      const status = getCertificateStatus(cert)
      setResult({ found: true, status, cert, name: studentName, course: courseName })
    } catch {
      setResult({ found: false })
    } finally {
      setVerifying(false)
    }
  }

  return (
    <div className="space-y-4 max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-navy flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Certificate Verification
          </CardTitle>
          <CardDescription>
            Enter the verification token to check the validity of a certificate.
            This corresponds to the <code className="bg-gray-50 px-1 rounded text-xs">verificationToken</code> field.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className={cn(inputClass, 'flex-1 font-mono text-xs')}
            />
            <Button onClick={handleVerify} disabled={verifying}>
              <Search className="w-4 h-4" />
              {verifying ? 'Verifying…' : 'Verify'}
            </Button>
          </div>

          {result !== null && (
            <div
              className={cn(
                'rounded-lg border p-4 space-y-3',
                !result.found
                  ? 'bg-red-50 border-red-200'
                  : result.status === 'valid'
                  ? 'bg-green-50 border-green-200'
                  : result.status === 'expired'
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-red-50 border-red-200'
              )}
            >
              {!result.found ? (
                <div className="flex items-center gap-2 text-red-700">
                  <XCircle className="w-5 h-5 shrink-0" />
                  <div>
                    <p className="font-semibold">Certificate not found</p>
                    <p className="text-sm">No certificate matches this token.</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    {result.status === 'valid' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                    ) : result.status === 'expired' ? (
                      <Clock className="w-5 h-5 text-yellow-600 shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 shrink-0" />
                    )}
                    <p className={cn('font-semibold', result.status === 'valid' ? 'text-green-700' : result.status === 'expired' ? 'text-yellow-700' : 'text-red-700')}>
                      {result.status === 'valid' ? 'Valid' : result.status === 'expired' ? 'Expired' : 'Revoked'}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    <div>
                      <span className="text-slate-500">Student:</span>
                      <span className="font-medium text-navy ml-1">{result.name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Course:</span>
                      <span className="font-medium text-navy ml-1">{result.course}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Issued:</span>
                      <span className="font-medium text-navy ml-1">{fmtDate(result.cert.issuedAt)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Valid until:</span>
                      <span className="font-medium text-navy ml-1">{fmtDate(result.cert.expiresAt)}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-slate-500">Cert. Number:</span>
                      <span className="font-mono text-xs font-medium text-navy ml-1">{result.cert.certificateNumber}</span>
                    </div>
                  </div>
                  {result.status === 'expired' && (
                    <p className="text-sm text-yellow-700 bg-yellow-100 rounded p-2">
                      This certificate has expired. Renewal requires new training.
                    </p>
                  )}
                  {result.status === 'revoked' && (
                    <p className="text-sm text-red-700 bg-red-100 rounded p-2">
                      This certificate has been revoked and is no longer valid.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
